import { Component, ElementRef, HostListener, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core'
import { FormControl, FormGroup, FormGroupDirective, Validators } from '@angular/forms'

interface Option {
    label?: string,
    value: string | number,
    selected?: boolean,
    disabled?: boolean
}

@Component({
    selector: 'app-form-select',
    templateUrl: 'form-select.html'
})
export class FormSelectComponent implements OnInit, OnChanges {

    @Input() label = 'Please select from the following'
    @Input() options: Option[]
    @Input() selected: string
    @Input() placeholderText = 'Select an option'
    @Input() fieldId = 'dropdown'
    @Input() darkTheme = false
    @Input() required = false
    @Input() modifierClassname: string
    @Input() useMinWidth = false
    @Input() maxHeight = '50vh'

    form: FormGroup
    selectedOption: Option
    showOptions = false
    touched = false

    @HostListener('document:click', ['$event'])
    clickout(event: Event) {
        const targetElement = event.target as HTMLInputElement

        if (!this.eRef.nativeElement.contains(targetElement)) {
            this.showOptions = false
        }
    }

    constructor(
        private rootFormGroup: FormGroupDirective,
        private eRef: ElementRef<HTMLElement>) {}

    ngOnInit(): void {
        this.selectedOption = this.options.filter(option => option.selected).map(option => option)[0]

        this.form = this.rootFormGroup.control

        const validators = this.getValidators()

        this.form.addControl(
            this.fieldId,
            new FormControl(this.selectedOption?.value || null, validators)
        )

        if (this.selected) {
            this.setSelectedValue()
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.required?.isFirstChange()) { return }
        if (changes?.required?.previousValue !== changes?.required?.currentValue) {
            if (changes.required.currentValue) {
                this.addValidators()
            } else {
                this.removeValidators()    
            }
        }

        if (changes?.selected?.previousValue !== changes?.selected?.currentValue) {
            this.selected = changes.selected.currentValue
            this.setSelectedValue()
        }
    }

    get control() {
        return this.form.get(this.fieldId)
    }

    setSelectedValue() {
        const selectedOption = this.options.find(option =>
            option.value.toString() === this.selected?.toString()
        )

        if (selectedOption) {
            this.onSelect(selectedOption)
        }
    }

    onSelect(option: Option) {
        this.selectedOption = option
        this.showOptions = false
        this.form.get(this.fieldId)?.setValue(this.selectedOption.value)
    }

    openDropdown(event: PointerEvent | KeyboardEvent | MouseEvent) {
        if (this.showOptions) {
            this.showOptions = false
        } else if (this.shouldOpenDropdown(event)) {
            this.showOptions = true
        }
    }

    isTouched() {
        this.touched = true
    }

    private shouldOpenDropdown(event: PointerEvent | KeyboardEvent | MouseEvent) {
        return (((event instanceof PointerEvent) || (event instanceof MouseEvent)) && event.type === 'click') ||
            (event instanceof PointerEvent && event.pointerType === 'touch') ||
            (event instanceof KeyboardEvent && event.type === 'keypress' && event.code === 'Space')
    }

    private getValidators() {
        return [
            this.required ? Validators.required : null
        ].filter(Boolean)
    }

    private removeValidators() {
        this.form.get(this.fieldId)?.clearValidators()
        this.form.get(this.fieldId)?.updateValueAndValidity()  
    }
         
    private addValidators() {
        this.form.get(this.fieldId)?.setValidators(this.getValidators())
        this.form.get(this.fieldId)?.updateValueAndValidity() 
    }
}
